import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

/** The fold id of the submitter's case, which belongs to the question and not to any answer. */
export const CASE_FOLD_ID = 'case';

const isAnswerFold = (id: string) => id !== CASE_FOLD_ID;

/** A references list is a fold that starts open, and stays open until the reader closes it. */
const isReferencesFold = (id: string) => id.startsWith('references-');

/** The open set with every answer fold removed, so only the question's case fold can remain. */
const keepQuestionFolds = (open: ReadonlySet<string>): ReadonlySet<string> =>
  new Set([...open].filter((id) => !isAnswerFold(id)));

/**
 * The open or closed state of every fold on a post, held in one place so a single control can
 * open them all. A fold is identified by a string, registers itself as collapsible once it knows
 * it has something to hide, and reads and flips its own state here.
 *
 * Only folds that hide something register, so "everything is open" means every fold that could
 * be closed is open, and the control is offered only when at least one could be.
 *
 * Whole answers fold too. With two or more answers each card can collapse to its header row, so
 * the control is offered even when no text folds. All answers start expanded. Expand all opens
 * every card and every text fold inside the answers, and Collapse all closes them again.
 *
 * The question's case fold is not an answer, so neither control touches it: it stays open or
 * closed as the reader left it. It never makes the control appear on its own either.
 */
export type AnswersFolds = {
  isOpen: (id: string) => boolean;
  toggle: (id: string) => void;
  setCollapsible: (id: string, collapsible: boolean) => void;
  hasCollapsible: boolean;
  allOpen: boolean;
  openAll: () => void;
  closeAll: () => void;
  /** Whether an answer, by key, is collapsed to its header row. */
  isAnswerCollapsed: (key: string) => boolean;
  toggleAnswer: (key: string) => void;
  expandAnswer: (key: string) => void;
  /** Whether the answers can collapse at all, which takes two or more. */
  answersCollapse: boolean;
};

const NO_FOLDS: AnswersFolds = {
  isOpen: () => false,
  toggle: () => undefined,
  setCollapsible: () => undefined,
  hasCollapsible: false,
  allOpen: false,
  openAll: () => undefined,
  closeAll: () => undefined,
  isAnswerCollapsed: () => false,
  toggleAnswer: () => undefined,
  expandAnswer: () => undefined,
  answersCollapse: false,
};

export const AnswersFoldsContext = createContext<AnswersFolds>(NO_FOLDS);

export const useAnswersFolds = () => useContext(AnswersFoldsContext);

/** Keys of the answers on the post, in order. Two or more are what make them collapsible. */
export function useAnswersFoldsState(answerKeys: readonly string[] = []): AnswersFolds {
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set());

  /** The reader's choice per references list, over `referencesOpenByDefault`. */
  const [referencesOverrides, setReferencesOverrides] = useState<ReadonlyMap<string, boolean>>(
    new Map()
  );

  /** Expand all and Collapse all set this for every references list at once. */
  const [referencesOpenByDefault, setReferencesOpenByDefault] = useState(true);

  const [collapsedAnswers, setCollapsedAnswers] = useState<ReadonlySet<string>>(new Set());

  /** After Expand all, folds inside answers that were collapsed open as they come back. */
  const openOnRegister = useRef(false);

  const answersCollapse = answerKeys.length >= 2;

  const answerKeysId = answerKeys.join('|');

  const [collapsible, setCollapsibleIds] = useState<ReadonlySet<string>>(new Set());

  const isOpen = useCallback(
    (id: string) =>
      isReferencesFold(id)
        ? (referencesOverrides.get(id) ?? referencesOpenByDefault)
        : open.has(id),
    [open, referencesOverrides, referencesOpenByDefault]
  );

  const toggle = useCallback(
    (id: string) => {
      openOnRegister.current = false;

      if (isReferencesFold(id)) {
        setReferencesOverrides((current) =>
          new Map(current).set(id, !(current.get(id) ?? referencesOpenByDefault))
        );

        return;
      }

      setOpen((current) => {
        const next = new Set(current);

        if (!next.delete(id)) {
          next.add(id);
        }

        return next;
      });
    },
    [referencesOpenByDefault]
  );

  const setCollapsible = useCallback((id: string, isCollapsible: boolean) => {
    if (isCollapsible && openOnRegister.current && isAnswerFold(id)) {
      setOpen((opened) => (opened.has(id) ? opened : new Set(opened).add(id)));
    }

    setCollapsibleIds((current) => {
      if (current.has(id) === isCollapsible) {
        return current;
      }

      const next = new Set(current);

      if (isCollapsible) {
        next.add(id);
      } else {
        next.delete(id);
      }

      return next;
    });
  }, []);

  const openAll = useCallback(() => {
    openOnRegister.current = true;

    setOpen((current) => {
      const next = new Set(keepQuestionFolds(current));

      for (const id of collapsible) {
        if (isAnswerFold(id)) {
          next.add(id);
        }
      }

      return next;
    });

    setReferencesOverrides(new Map());

    setReferencesOpenByDefault(true);

    setCollapsedAnswers(new Set());
  }, [collapsible]);

  const closeAll = useCallback(() => {
    openOnRegister.current = false;

    setOpen(keepQuestionFolds);

    setReferencesOverrides(new Map());

    setReferencesOpenByDefault(false);

    if (answersCollapse) {
      setCollapsedAnswers(new Set(answerKeysId.split('|')));
    }
  }, [answersCollapse, answerKeysId]);

  const isAnswerCollapsed = useCallback(
    (key: string) => answersCollapse && collapsedAnswers.has(key),
    [answersCollapse, collapsedAnswers]
  );

  const toggleAnswer = useCallback((key: string) => {
    setCollapsedAnswers((current) => {
      const next = new Set(current);

      if (!next.delete(key)) {
        next.add(key);
      }

      return next;
    });
  }, []);

  const expandAnswer = useCallback((key: string) => {
    setCollapsedAnswers((current) => {
      if (!current.has(key)) {
        return current;
      }

      const next = new Set(current);

      next.delete(key);

      return next;
    });
  }, []);

  const answerFolds = [...collapsible].filter(isAnswerFold);

  const anyAnswerCollapsed =
    answersCollapse && answerKeysId.split('|').some((key) => collapsedAnswers.has(key));

  return useMemo(
    () => ({
      isOpen,
      toggle,
      setCollapsible,
      hasCollapsible: answerFolds.length > 0 || answersCollapse,
      // With answers that collapse, only the cards decide the label. Text folds never do. A lone
      // answer has no cards to collapse, so its own text folds still decide. The case never does.
      allOpen: answersCollapse
        ? !anyAnswerCollapsed
        : answerFolds.length > 0 && answerFolds.every((id) => open.has(id)),
      openAll,
      closeAll,
      isAnswerCollapsed,
      toggleAnswer,
      expandAnswer,
      answersCollapse,
    }),
    [
      isOpen,
      toggle,
      setCollapsible,
      collapsible,
      answersCollapse,
      anyAnswerCollapsed,
      open,
      openAll,
      closeAll,
      isAnswerCollapsed,
      toggleAnswer,
      expandAnswer,
    ]
  );
}
